import { TypedDocumentNode, gql } from '@apollo/client';
import BigNumber from 'bignumber.js';

import { FiatCurrency, SupportedCurrency } from '__generated__/globalTypes';
import { BigToBigInt } from 'bigint';

import {
  MonetaryAmountFragment_monetaryAmount,
  RatesFragment_rates,
} from './__generated__/monetaryAmount.graphql';
import { fromWei } from './wei';

export interface MonetaryAmountParams {
  referenceCurrency: SupportedCurrency;
  eurCents?: number | string | null;
  usdCents?: number | string | null;
  gbpCents?: number | string | null;
  wei?: bigint | string | null;
}

const ETH_IN_WEI = new BigNumber('1000000000000000000');
const ETH_PRECISION = 4;

type MonetaryAmountCurrency = 'eurCents' | 'usdCents' | 'gbpCents' | 'wei';

type MonetaryAmountFiatCurrency = 'eurCents' | 'usdCents' | 'gbpCents';

const getFiatMonetaryAmountIndex = (
  currency: FiatCurrency
): MonetaryAmountFiatCurrency => {
  return `${currency.toLowerCase()}Cents` as MonetaryAmountFiatCurrency;
};

const getMonetaryAmountIndex = (
  currency: SupportedCurrency
): MonetaryAmountCurrency => {
  return currency === SupportedCurrency.WEI
    ? 'wei'
    : getFiatMonetaryAmountIndex(currency);
};

export const getMinorRate = (
  rates: RatesFragment_rates,
  currency: FiatCurrency
) => {
  const key = getFiatMonetaryAmountIndex(currency);
  return new BigNumber(rates[key]);
};

const rate = (
  referenceCurrency: SupportedCurrency,
  currency: SupportedCurrency,
  rates: RatesFragment_rates
) => {
  if (referenceCurrency === SupportedCurrency.WEI) {
    return getMinorRate(rates, currency as unknown as FiatCurrency);
  }
  if (currency === SupportedCurrency.WEI) {
    return new BigNumber(1).dividedBy(getMinorRate(rates, referenceCurrency));
  }

  return getMinorRate(rates, currency).dividedBy(
    getMinorRate(rates, referenceCurrency)
  );
};

const convert = (
  referenceCurrency: SupportedCurrency,
  referenceAmount: bigint | number | string,
  currency: SupportedCurrency,
  rates: RatesFragment_rates
): BigNumber => {
  if (referenceCurrency === currency)
    return new BigNumber(referenceAmount.toString());

  if (referenceCurrency === SupportedCurrency.WEI) {
    return new BigNumber(referenceAmount.toString())
      .dividedBy(ETH_IN_WEI)
      .multipliedBy(rate(referenceCurrency, currency, rates))
      .decimalPlaces(0, BigNumber.ROUND_HALF_UP);
  }
  if (currency === SupportedCurrency.WEI) {
    return new BigNumber(referenceAmount.toString())
      .multipliedBy(rate(referenceCurrency, currency, rates))
      .decimalPlaces(ETH_PRECISION, BigNumber.ROUND_HALF_UP)
      .multipliedBy(ETH_IN_WEI);
  }

  return new BigNumber(referenceAmount.toString())
    .multipliedBy(rate(referenceCurrency, currency, rates))
    .decimalPlaces(0, BigNumber.ROUND_HALF_UP);
};

const bigValueAbove = (
  referenceCurrency: SupportedCurrency,
  referenceValue: bigint | number | string,
  currency: SupportedCurrency,
  rates: RatesFragment_rates
): BigNumber => {
  let bigValue = convert(referenceCurrency, referenceValue, currency, rates);
  const step =
    currency === SupportedCurrency.WEI
      ? new BigNumber(ETH_IN_WEI).dividedBy(10 ** ETH_PRECISION)
      : 1;
  let bigValueInReferenceCurrency: BigNumber;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    bigValueInReferenceCurrency = convert(
      currency,
      bigValue.toNumber(),
      referenceCurrency,
      rates
    );

    if (
      bigValueInReferenceCurrency.isNaN() ||
      bigValueInReferenceCurrency.isZero()
    ) {
      return new BigNumber(0);
    }
    if (
      bigValueInReferenceCurrency.isGreaterThanOrEqualTo(
        referenceValue!.toString()
      )
    ) {
      return bigValue;
    }

    bigValue = bigValue.plus(step);
  }
};

const inCurrency = <T extends SupportedCurrency>(
  referenceCurrency: SupportedCurrency,
  referenceValue: bigint | number | string,
  currency: T,
  rates: RatesFragment_rates
): T extends typeof SupportedCurrency.WEI ? bigint : number => {
  if (referenceCurrency === currency) {
    return (
      currency === SupportedCurrency.WEI
        ? BigInt(referenceValue)
        : Number(referenceValue)
    ) as T extends typeof SupportedCurrency.WEI ? bigint : number;
  }

  const value = bigValueAbove(
    referenceCurrency,
    referenceValue,
    currency,
    rates
  );

  return (
    currency === SupportedCurrency.WEI ? BigToBigInt(value) : value.toNumber()
  ) as T extends typeof SupportedCurrency.WEI ? bigint : number;
};

export class MonetaryAmount {
  public static fromRates(
    params: MonetaryAmountParams,
    rates: RatesFragment_rates
  ) {
    const { referenceCurrency } = params;
    const index = getMonetaryAmountIndex(referenceCurrency);
    if (params[index] === null || params[index] === undefined)
      throw new Error('Reference currency is required');

    const referenceValue = params[index]!;

    return new MonetaryAmount({
      referenceCurrency,
      wei: params.wei
        ? BigInt(params.wei)
        : inCurrency(
            referenceCurrency,
            referenceValue,
            SupportedCurrency.WEI,
            rates
          ),
      eurCents:
        Number(params.eurCents) ||
        inCurrency(
          referenceCurrency,
          referenceValue,
          SupportedCurrency.EUR,
          rates
        ),
      usdCents:
        Number(params.usdCents) ||
        inCurrency(
          referenceCurrency,
          referenceValue,
          SupportedCurrency.USD,
          rates
        ),
      gbpCents:
        Number(params.gbpCents) ||
        inCurrency(
          referenceCurrency,
          referenceValue,
          SupportedCurrency.GBP,
          rates
        ),
    });
  }

  public static fromReferenceCurrency(
    referenceCurrency: SupportedCurrency,
    referenceValue: bigint | number | string,
    rates: RatesFragment_rates
  ) {
    return MonetaryAmount.fromRates(
      {
        referenceCurrency,
        [getMonetaryAmountIndex(referenceCurrency)]: referenceValue,
      },
      rates
    );
  }

  public static zero = new MonetaryAmount({
    referenceCurrency: SupportedCurrency.WEI,
    wei: 0n,
    eurCents: 0,
    usdCents: 0,
    gbpCents: 0,
  });

  referenceCurrency: SupportedCurrency;

  eurCents: number;

  usdCents: number;

  gbpCents: number;

  wei: bigint;

  isPositive: boolean;

  isZero: boolean;

  constructor(params: {
    referenceCurrency: SupportedCurrency;
    wei: bigint;
    eurCents: number;
    usdCents: number;
    gbpCents: number;
  }) {
    this.referenceCurrency = params.referenceCurrency;
    this.wei = params.wei;
    this.eurCents = params.eurCents;
    this.usdCents = params.usdCents;
    this.gbpCents = params.gbpCents;
    this.isPositive = this.wei > 0n;
    this.isZero = this.wei === 0n;
  }

  get eur() {
    return this.eurCents / 100;
  }

  get usd() {
    return this.usdCents / 100;
  }

  get gbp() {
    return this.gbpCents / 100;
  }

  get eth() {
    return fromWei(this.wei);
  }

  get referenceValue() {
    return this[getMonetaryAmountIndex(this.referenceCurrency)];
  }

  gt(other: MonetaryAmount) {
    return this.wei > other.wei;
  }

  gte(other: MonetaryAmount) {
    return this.wei >= other.wei;
  }

  readAmount<T extends SupportedCurrency>(
    currency: T
  ): T extends FiatCurrency ? number : bigint {
    const key = getMonetaryAmountIndex(currency);

    return this[key] as T extends FiatCurrency ? number : bigint;
  }

  toMajorUnit(currency: FiatCurrency) {
    return this.readAmount(currency) / 100;
  }

  withDiscount(percentageDiscount: number, maxDiscounts: MonetaryAmount) {
    return new MonetaryAmount({
      referenceCurrency: this.referenceCurrency,
      wei: BigInt(
        BigNumber.minimum(
          new BigNumber(this.wei.toString())
            .multipliedBy(percentageDiscount)
            .decimalPlaces(0),
          new BigNumber(maxDiscounts.wei.toString())
        ).toString()
      ),
      eurCents: BigNumber.minimum(
        new BigNumber(this.eurCents.toString())
          .multipliedBy(percentageDiscount)
          .decimalPlaces(0),
        new BigNumber(maxDiscounts.eurCents.toString())
      ).toNumber(),
      usdCents: BigNumber.minimum(
        new BigNumber(this.usdCents.toString())
          .multipliedBy(percentageDiscount)
          .decimalPlaces(0),
        new BigNumber(maxDiscounts.usdCents.toString())
      ).toNumber(),
      gbpCents: BigNumber.minimum(
        new BigNumber(this.gbpCents.toString())
          .multipliedBy(percentageDiscount)
          .decimalPlaces(0),
        new BigNumber(maxDiscounts.gbpCents.toString())
      ).toNumber(),
    });
  }

  isEqualTo(other: MonetaryAmount) {
    return (
      this.wei === other.wei &&
      this.eurCents === other.eurCents &&
      this.usdCents === other.usdCents &&
      this.gbpCents === other.gbpCents
    );
  }

  multipiedBy(factor: number) {
    return new MonetaryAmount({
      referenceCurrency: this.referenceCurrency,
      wei: this.wei * BigInt(factor),
      eurCents: this.eurCents * factor,
      usdCents: this.usdCents * factor,
      gbpCents: this.gbpCents * factor,
    });
  }

  calculateFees = (
    feesPercentagePoints: bigint
  ): {
    feesMonetaryAmount: MonetaryAmount;
    youReceiveMonetaryAmount: MonetaryAmount;
  } => {
    const feesMonetaryAmount =
      feesPercentagePoints === 0n
        ? MonetaryAmount.zero
        : new MonetaryAmount({
            eurCents:
              Number(BigInt(this.eurCents) * feesPercentagePoints) / 100,
            usdCents:
              Number(BigInt(this.usdCents) * feesPercentagePoints) / 100,
            gbpCents:
              Number(BigInt(this.gbpCents) * feesPercentagePoints) / 100,
            wei: (this.wei * feesPercentagePoints) / 100n,
            referenceCurrency: this.referenceCurrency,
          });
    const youReceiveMonetaryAmount = new MonetaryAmount({
      eurCents: this.eurCents - feesMonetaryAmount.eurCents,
      usdCents: this.usdCents - feesMonetaryAmount.usdCents,
      gbpCents: this.gbpCents - feesMonetaryAmount.gbpCents,
      wei: this.wei - feesMonetaryAmount.wei,
      referenceCurrency: this.referenceCurrency,
    });

    return {
      feesMonetaryAmount,
      youReceiveMonetaryAmount,
    };
  };
}

export const monetaryAmountFragment = gql`
  fragment MonetaryAmountFragment_monetaryAmount on MonetaryAmount {
    eurCents
    usdCents
    gbpCents
    wei
    referenceCurrency
  }
` as TypedDocumentNode<MonetaryAmountFragment_monetaryAmount>;

export const ratesFragment = gql`
  fragment RatesFragment_rates on Rates {
    eurCents
    usdCents
    gbpCents
  }
` as TypedDocumentNode<RatesFragment_rates>;
